import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Form, Input, Table } from 'tdesign-react';
import { produce } from 'immer';
import ShadowCard from '@/components/ShadowCard';
import useTable from '@/hooks/useTable';
import {
  createSource,
  doSourceRestart,
  doSourceStop,
  getPackageList,
  getSourceChart,
  getSourceConfigDetail,
  getSourceInstances,
  getSourceList,
  getSourceParams,
  getSourceStatus,
  updateSourceConfig,
} from '@/services/functionService';
import ModalHeader from '@/components/Header/Modal';
import { isValidSimpleJSONRC } from '@/utils/common';
import PermWrapper from '@/components/PermWrapper';
import RCForm from '@/components/Form';
import ColTip from '@/components/Tip';
import JSONConverterInput from '@/components/Form/JSONConverterInput';
import FormModal from '../../components/FormModal';
import { defaultColumns } from '../../identical';
import Options from '../../components/Options';
import TNItems from '../../components/TNItems';
import Detail from './Detail';
import Instance from './Instance';

const { FormItem } = RCForm;

export default function Body() {
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);

  const clusterName = searchParams.get('name');

  // 操作项里的各种请求
  const getOptionRequests = (row) => ({
    // 看板
    getChartsData: (params) =>
      getSourceChart({
        clusterName,
        namespace: `${row.tenant}/${row.namespace}`,
        sourceName: row.sourceName,
        ...params,
      }), // 包管理列表
    getPackageList: (val) =>
      getPackageList({
        clusterName,
        packageType: 'Source',
        functionRuntime: val,
      }),
    getConfigDetail: async () => {
      const { data } = await getSourceConfigDetail({
        clusterName,
        sourceName: row.sourceName,
      });
      return data;
    },
    updateFunctionConfig: (values) =>
      updateSourceConfig({
        clusterName,
        ...values,
        sourceConfig: JSON.parse(values.sourceConfig || null),
      }),
    doRestart: () =>
      doSourceRestart({
        clusterName,
        sourceName: row.sourceName,
      }),
    doStop: () =>
      doSourceStop({
        clusterName,
        sourceName: row.sourceName,
      }),
    getStatus: () => getSourceStatus({ clusterName, sourceName: row.sourceName }),
    getParams: () => getSourceParams({ clusterName, sourceName: row.sourceName }),
  });

  // 自定义信息
  const getDiffNode = (row) => ({
    dashboardHeader: (
      <ModalHeader tags={[`${row.tenant}/${row.namespace}/${row.sourceName}`, clusterName]}>
        监控-Source
      </ModalHeader>
    ),
    packageHeader: (
      <ModalHeader tags={[`${row.tenant}/${row.namespace}/${row.sourceName}`, clusterName]}>
        包管理
      </ModalHeader>
    ),
    startHeader: (
      <ModalHeader tags={[`${row.tenant}/${row.namespace}/${row.sourceName}`, clusterName]}>
        启动
      </ModalHeader>
    ),
    startFormSnipPart1: (
      <>
        <FormItem name='className' label='函数入口'>
          <Input />
        </FormItem>
        <FormItem name='destinationTopic' label='目标Topic' rules={[{ required: true }]}>
          <Input placeholder='单个Topic' />
        </FormItem>
      </>
    ),
    startFormSnipPart2: (
      <>
        <FormItem
          label='Source配置'
          name='sourceConfig'
          rules={[
            { required: true },
            {
              validator: isValidSimpleJSONRC,
              message: '请输入[key, value]结构的json格式，不允许嵌套',
            },
          ]}
        >
          <JSONConverterInput />
        </FormItem>
      </>
    ),
    startFormSnipPart3: (
      <>
        <FormItem
          label={
            <ColTip
              content={
                <>
                  <strong>生产端参数-默认不需要配置</strong>
                  <li>1、往下游进行批量生产时，构造生产对象所需的参数，支持配置参数如下：</li>
                  <li>2、maxPendingMessages（单分区），默认值为1000。</li>
                  <li>3、maxPendingMessagesAcrossPartitions（多分区），默认值为50000。</li>
                  <li>4、其它核心参数不允许配置的默认值如下：</li>
                  <li>5、blockIfQueueFull为true。</li>
                  <li>6、sendTimeout为0，永不超时。</li>
                  <li>7、compressionType为CompressionType.LZ4。</li>
                </>
              }
            >
              生产端参数
            </ColTip>
          }
          name='producerConfig'
          rules={[
            {
              validator: isValidSimpleJSONRC,
              message: '请输入[key, value]结构的json格式，不允许嵌套',
            },
          ]}
        >
          <JSONConverterInput />
        </FormItem>
      </>
    ),
  });

  const columns = produce(defaultColumns, (draft) => {
    // 第一列
    const taskColumn = draft[0];
    taskColumn.colKey = 'sourceName';
    taskColumn.cell = ({ row }) => (
      <Detail row={row} clusterName={clusterName} onRefresh={doRefresh} />
    );

    // 实例个数列
    const instanceNumColumn = draft.find((item) => item.colKey === 'instanceNum');
    instanceNumColumn.cell = ({ row }) => (
      <Instance
        row={row}
        request={() => {
          return getSourceInstances({
            clusterName,
            sourceName: row.sourceName,
          });
        }}
      />
    );

    // 操作列
    const optionColumn = draft.find((item) => item.colKey === 'option');
    optionColumn.cell = ({ row }) => (
      <Options
        row={row}
        resource='pulsar:function:source'
        getOptionRequests={getOptionRequests}
        getDiffNode={getDiffNode}
        doRefresh={doRefresh}
        clusterName={clusterName}
        functionType='source'
      />
    );
  });

  const loadData = useMemoizedFn(async (params = {}) => {
    const { tenant, namespace } = params;
    const query = { ...params, clusterName };
    if (namespace) {
      query.namespace = namespace.replace(`${tenant}/`, ''); // 剔除 租户/ 打头
    }
    const { data } = await getSourceList(query);
    setData(
      data.map((item) => ({
        ...item,
        sourceName: item.functionName,
      })),
    );
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <TNItems clusterName={clusterName} />
        <Form.FormItem name='sourceName'>
          <Input placeholder='请输入名称' clearable />
        </Form.FormItem>
      </>
    ),
    operators: (
      <PermWrapper resource='pulsar:function:source:create'>
        <Button theme='primary' onClick={() => setVisible(true)}>
          创建任务
        </Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch, clusterName]);

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <FormModal
        request={createSource}
        visible={visible}
        handleClose={handleClose}
        clusterName={clusterName}
      />
    </Root>
  );
}

const Root = styled(ShadowCard)`
  margin-top: 12px;
`;
